import { createUseCRUDService, ICRUDService } from '../../reusableFeatures';
import { backendServices } from '../../services';
import { ERPGatewayConfigurationApi } from '../../services/backendServices/Apis';
import {
    ERPGatewayConfigurationFormViewModel,
    ERPGatewayConfigurationViewModel,
} from '../../services/backendServices/ViewModels/ERPGatewayConfigurationViewModel';

export class ERPGatewayConfigurationCRUDService implements ICRUDService<number, ERPGatewayConfigurationFormViewModel> {
    private api = new ERPGatewayConfigurationApi();

    get defaultState(): ERPGatewayConfigurationFormViewModel {
        return {
            CompanyId: 0,
            InvoiceProfileId: 0,
            BuyerDestinationProfileId: 0,
            InvoiceCheckFrequency: null,
            InvoiceCheckTimeOfDay: '',
            POCheckFrequency: null,
            POCheckTimeOfDay: '',
            ERPName: '',
            ERPVersion: '',
            InvoiceWorkingDirectory: '',
            POWorkingDirectory: '',
            ERPConnectionInfo: { root: {} },
            Active: false,
        };
    }

    get key() {
        return 'ERPGatewayConfiguration';
    }

    get invalidateKeys(): string[] {
        return ['ERPGatewayConfiguration'];
    }

    async fetchEntity(id: number): Promise<ERPGatewayConfigurationFormViewModel> {
        const response = await this.api.getERPGatewayConfiguration(id);

        const entity = response.data;

        return this.mapFromViewModel(entity);
    }

    async updateEntity(id: number, state: ERPGatewayConfigurationFormViewModel): Promise<void> {
        const parsedForm = backendServices.ViewModels.ERPGatewayConfigurationFormSchema.safeParse(state);

        if (!parsedForm.success) {
            let friendlyErrorMessage = '\nError(s) occured while updating ERP Gateway Configuration:\n';

            const errors = parsedForm.error.errors;
            const formattedErrors = errors.map((error) => {
                const message = error.message;

                return `${message}`;
            });

            // format errors for user
            friendlyErrorMessage += formattedErrors.map((error) => `- ${error}`).join('\n');

            throw new Error(friendlyErrorMessage);
        }

        const viewModel = this.mapToViewModel(state, id);
        await this.api.putERPGatewayConfiguration(id, viewModel);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async createEntity(state: ERPGatewayConfigurationFormViewModel): Promise<number> {
        throw new Error('not implemented');
    }

    async deleteEntity(): Promise<void> {
        throw new Error('not implemented');
    }

    private mapFromViewModel(viewModel: ERPGatewayConfigurationViewModel): ERPGatewayConfigurationFormViewModel {
        return {
            CompanyId: viewModel.CompanyId,
            InvoiceProfileId: viewModel.InvoiceProfileId,
            BuyerDestinationProfileId: viewModel.BuyerDestinationProfileId,
            InvoiceCheckFrequency: viewModel.InvoiceCheckFrequency,
            InvoiceCheckTimeOfDay: viewModel.InvoiceCheckTimeOfDay,
            POCheckFrequency: viewModel.POCheckFrequency,
            POCheckTimeOfDay: viewModel.POCheckTimeOfDay,
            ERPName: viewModel.ERPName,
            ERPVersion: viewModel.ERPVersion,
            InvoiceWorkingDirectory: viewModel.InvoiceWorkingDirectory,
            POWorkingDirectory: viewModel.POWorkingDirectory,
            ERPConnectionInfo: viewModel.ERPConnectionInfo,
            Active: viewModel.Active,
        };
    }

    private mapToViewModel(
        formData: ERPGatewayConfigurationFormViewModel,
        id: number | null = null
    ): ERPGatewayConfigurationViewModel {
        return {
            Id: id,
            CompanyId: formData.CompanyId,
            InvoiceProfileId: formData.InvoiceProfileId,
            BuyerDestinationProfileId: formData.BuyerDestinationProfileId,
            InvoiceCheckFrequency: formData.InvoiceCheckFrequency,
            InvoiceCheckTimeOfDay: formData.InvoiceCheckTimeOfDay,
            POCheckFrequency: formData.POCheckFrequency,
            POCheckTimeOfDay: formData.POCheckTimeOfDay,
            ERPName: formData.ERPName,
            ERPVersion: formData.ERPVersion,
            InvoiceWorkingDirectory: formData.InvoiceWorkingDirectory,
            POWorkingDirectory: formData.POWorkingDirectory,
            ERPConnectionInfo: formData.ERPConnectionInfo,
            Active: formData.Active,
        };
    }
}

export const useERPGatewayConfigurationCRUDService = createUseCRUDService(ERPGatewayConfigurationCRUDService);
